import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Bell,
  Check,
  CheckCheck,
  Trash2,
  X,
  Target,
  Trophy,
  Flame,
  Award,
  AlertTriangle,
  AlertCircle,
  ArrowUpRight,
  Flag,
  TrendingUp,
  UserPlus,
  Users,
  CheckCircle,
  Activity,
  Info,
  Sparkles,
  Settings,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  target: Target,
  trophy: Trophy,
  flame: Flame,
  award: Award,
  "alert-triangle": AlertTriangle,
  "alert-circle": AlertCircle,
  "arrow-up-right": ArrowUpRight,
  flag: Flag,
  "trending-up": TrendingUp,
  "user-plus": UserPlus,
  users: Users,
  "check-circle": CheckCircle,
  activity: Activity,
  info: Info,
  sparkles: Sparkles,
};

const COLORS = {
  emerald: "text-emerald-400 bg-emerald-400/20",
  yellow: "text-yellow-400 bg-yellow-400/20",
  orange: "text-orange-400 bg-orange-400/20",
  purple: "text-purple-400 bg-purple-400/20",
  amber: "text-amber-400 bg-amber-400/20",
  red: "text-red-400 bg-red-400/20",
  blue: "text-blue-400 bg-blue-400/20",
  gray: "text-gray-400 bg-gray-400/20",
};

export default function NotificationCenter({ token }) {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [summary, setSummary] = useState({ total_unread: 0, high_priority: 0 });
  const [loading, setLoading] = useState(false);
  const wsRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/notification-center?limit=50`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API}/notification-center/summary`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch (error) {
      console.error("Failed to fetch summary:", error);
    }
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API}/notification-center/${notificationId}/read`, {
        method: "POST",
        credentials: "include",
      });
      
      setNotifications(prev =>
        prev.map(n =>
          n.notification_id === notificationId ? { ...n, read: true } : n
        )
      );
      setSummary(prev => ({
        ...prev,
        total_unread: Math.max(0, prev.total_unread - 1)
      }));
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${API}/notification-center/read-all`, {
        method: "POST",
        credentials: "include",
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setSummary(prev => ({ ...prev, total_unread: 0 }));
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await fetch(`${API}/notification-center/${notificationId}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      setNotifications(prev =>
        prev.filter(n => n.notification_id !== notificationId)
      );
    } catch (error) {
      console.error("Failed to delete notification:", error);
    }
  };

  const clearAll = async () => {
    try {
      await fetch(`${API}/notification-center`, {
        method: "DELETE",
        credentials: "include",
      });
      
      setNotifications([]);
      setSummary({ total_unread: 0, high_priority: 0 });
    } catch (error) {
      console.error("Failed to clear notifications:", error);
    }
  };

  // WebSocket connection for real-time updates
  const connectWebSocket = useCallback(() => {
    if (!token) return;
    
    const wsUrl = process.env.REACT_APP_BACKEND_URL
      ?.replace("https://", "wss://")
      .replace("http://", "ws://");
    
    if (!wsUrl) return;
    
    try {
      const ws = new WebSocket(`${wsUrl}/api/ws?token=${token}`);
      
      ws.onopen = () => {
        console.log("Notification WebSocket connected");
      };
      
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.event === "notification") {
            // Add new notification
            setNotifications(prev => [message.data, ...prev]);
            setSummary(prev => ({
              ...prev,
              total_unread: prev.total_unread + 1
            }));
          }
        } catch (e) {
          // Ignore parse errors
        }
      };
      
      ws.onclose = () => {
        // Reconnect after 5 seconds
        setTimeout(connectWebSocket, 5000);
      };
      
      wsRef.current = ws;
    } catch (e) {
      console.error("WebSocket connection failed:", e);
    }
  }, [token]);

  useEffect(() => {
    fetchSummary();
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connectWebSocket, fetchSummary]);

  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getIcon = (typeInfo) => {
    const IconComponent = ICONS[typeInfo?.icon] || Bell;
    return IconComponent;
  };

  const getColor = (typeInfo) => {
    return COLORS[typeInfo?.color] || COLORS.gray;
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-gray-400 hover:text-white hover:bg-white/10"
          data-testid="notification-center-btn"
        >
          <Bell className="w-5 h-5" />
          {summary.total_unread > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              {summary.total_unread > 9 ? "9+" : summary.total_unread}
            </span>
          )}
        </Button>
      </SheetTrigger>
      
      <SheetContent className="bg-[#0A0A0A] border-white/10 w-full sm:max-w-md">
        <SheetHeader className="border-b border-white/10 pb-4">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-white flex items-center gap-2">
              <Bell className="w-5 h-5 text-emerald-400" />
              Notifications
              {summary.total_unread > 0 && (
                <span className="px-2 py-0.5 text-xs bg-emerald-500/20 text-emerald-400 rounded-full">
                  {summary.total_unread} new
                </span>
              )}
            </SheetTitle>
            <div className="flex items-center gap-1">
              {notifications.some(n => !n.read) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  className="text-xs text-gray-400 hover:text-white"
                >
                  <CheckCheck className="w-4 h-4 mr-1" />
                  Mark all read
                </Button>
              )}
            </div>
          </div>
        </SheetHeader>
        
        <ScrollArea className="h-[calc(100vh-140px)] mt-4">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="p-4 bg-white/5 rounded-lg animate-pulse">
                  <div className="h-4 w-32 bg-white/10 rounded mb-2" />
                  <div className="h-3 w-full bg-white/10 rounded" />
                </div>
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="w-12 h-12 text-gray-700 mx-auto mb-4" />
              <p className="text-gray-500">No notifications yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {notifications.map(notification => {
                const Icon = getIcon(notification.type_info);
                const colorClass = getColor(notification.type_info);
                
                return (
                  <div
                    key={notification.notification_id}
                    data-testid={`notification-${notification.notification_id}`}
                    className={`p-4 rounded-lg transition-colors group ${
                      notification.read
                        ? "bg-white/5"
                        : "bg-white/10 border-l-2 border-emerald-500"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="text-sm font-medium text-white truncate">
                            {notification.title}
                          </h4>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {!notification.read && (
                              <button
                                onClick={() => markAsRead(notification.notification_id)}
                                className="p-1 hover:bg-white/10 rounded text-gray-400 hover:text-white"
                              >
                                <Check className="w-3 h-3" />
                              </button>
                            )}
                            <button
                              onClick={() => deleteNotification(notification.notification_id)}
                              className="p-1 hover:bg-red-500/20 rounded text-gray-400 hover:text-red-400"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          {notification.message}
                        </p>
                        <span className="text-xs text-gray-600 mt-2 block">
                          {formatTime(notification.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
        
        {notifications.length > 0 && (
          <div className="border-t border-white/10 pt-4 mt-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAll}
              className="w-full text-gray-400 hover:text-red-400 hover:bg-red-500/10"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear all notifications
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
